import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import Sidebar from "../../components/Dashboard/Sidebar";
import axios from "axios";
import toast from "react-hot-toast";
import {
  FiDollarSign,
  FiBox,
  FiLayers,
  FiHash,
  FiTruck,
  FiUpload,
  FiImage,
  FiPackage,
  FiX,
} from "react-icons/fi";
import { useAuth } from "../../context/auth";
import { useNavigate } from "react-router-dom";

const MAX_IMAGE_SIZE = 1024 * 1024; // 1MB

const CreateProduct = () => {
  const [auth] = useAuth();
  const navigate = useNavigate();
  // Removed sidebarOpen state
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    actualPrice: "",
    price: "",
    category: "",
    quantity: "",
    stock: "",
    shipping: "",
    sizes: [],
    colors: [],
  });
  const [sizeInput, setSizeInput] = useState("");

  const [colorInput, setColorInput] = useState({
    name: "",
    hex: "#000000",
  });
  const [photos, setPhotos] = useState([]);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const addSize = () => {
    const size = sizeInput.trim();

    if (!size) return;

    if (form.sizes.includes(size)) {
      return toast.error("Size already exists");
    }

    setForm({
      ...form,
      sizes: [...form.sizes, size],
    });

    setSizeInput("");
  };

  const removeSize = (size) => {
    setForm({
      ...form,
      sizes: form.sizes.filter((s) => s !== size),
    });
  };

  const addColor = () => {
    const name = colorInput.name.trim();

    if (!name) return;

    if (form.colors.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      return toast.error("Color already exists");
    }

    setForm({
      ...form,
      colors: [...form.colors, colorInput],
    });

    setColorInput({
      name: "",
      hex: "#000000",
    });
  };

  const removeColor = (name) => {
    setForm({
      ...form,
      colors: form.colors.filter((c) => c.name !== name),
    });
  };
  // Fetch categories for dropdown
  useEffect(() => {
    axios
      .get("/api/v1/category/get-category")
      .then((res) => {
        if (res.data?.success) setCategories(res.data.category);
      })
      .catch(() => toast.error("Failed to load categories"));
  }, []);

  // Remove image at index
  const removeImage = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
    setPreview((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    let allFiles = [...photos];
    let allPreview = [...preview];
    for (let file of files) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(
          `"${file.name}" is ${(file.size / 1024 ** 2).toFixed(
            2,
          )}MB. Each photo must be less than 1MB.`,
        );
        continue;
      }
      allFiles.push(file);
      allPreview.push(URL.createObjectURL(file));
    }
    setPhotos(allFiles);
    setPreview(allPreview);
    // Clear file input value so same file can be re-picked
    e.target.value = "";
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    for (let key of [
      "name",
      "description",
      "actualPrice",
      "price",
      "category",
      "quantity",
      "stock",
    ]) {
      if (!form[key])
        return toast.error(
          `${key[0].toUpperCase() + key.slice(1)} is required`,
        );
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value);
        }
      });
      photos.forEach((photo) => data.append("photos", photo));
      const res = await axios.post("/api/v1/product/create-product", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        toast.success("Product Created");
        navigate(`/dashboard/admin/products`);
        setForm({
          name: "",
          description: "",
          actualPrice: "",
          price: "",
          category: "",
          quantity: "",
          stock: "",
          shipping: "",
          sizes: [],
          colors: [],
        });
        setPhotos([]);
        setPreview([]);
      } else {
        toast.error(res.data.message || "Failed to create product");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Error creating product";
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  return (
    <Layout title="Create Product">
      <div className="min-h-screen bg-gray-50  mt-20">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Create Product
              </h1>
              <p className="text-gray-600 mt-2">
                Enter product details to add a new product.
              </p>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:gap-8">
            {/* Sidebar */}
            <aside className="w-full lg:w-80  flex-shrink-0 mb-6 lg:mb-0">
              <Sidebar
                auth={auth}
                activeTab="products"
                // setSidebarOpen={setSidebarOpen} // Removed
              />
            </aside>
            {/* Main Form */}
            <main className="flex-1">
              <div className="bg-white rounded-xl shadow border border-gray-100 p-8">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6 flex-grow flex flex-col"
                >
                  <div className="flex-grow space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Product Name with Icon */}
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Product Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiBox className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Enter product name"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      {/* Actual Price */}
                      <div>
                        <label
                          htmlFor="actualPrice"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Actual Price (MRP)
                        </label>

                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiDollarSign className="text-gray-400" size={18} />
                          </div>

                          <input
                            type="number"
                            id="actualPrice"
                            name="actualPrice"
                            value={form.actualPrice}
                            onChange={handleChange}
                            min="0"
                            placeholder="Enter MRP"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      {/* Price */}
                      <div>
                        <label
                          htmlFor="price"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Selling Price
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiDollarSign className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="number"
                            id="price"
                            name="price"
                            value={form.price}
                            onChange={handleChange}
                            min="0"
                            placeholder="Enter price"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      {/* Category */}
                      <div>
                        <label
                          htmlFor="category"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Category
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiLayers className="text-gray-400" size={18} />
                          </div>
                          <select
                            id="category"
                            name="category"
                            value={form.category}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          >
                            <option value="">Select category</option>
                            {categories.map((c) => (
                              <option value={c._id} key={c._id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {/* Stock */}
                      <div>
                        <label
                          htmlFor="stock"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Stock
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiPackage className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="number"
                            id="stock"
                            name="stock"
                            value={form.stock}
                            min="0"
                            onChange={handleChange}
                            placeholder="Stock"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      {/* Quantity */}
                      <div>
                        <label
                          htmlFor="quantity"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Quantity
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiHash className="text-gray-400" size={18} />
                          </div>
                          <input
                            type="number"
                            id="quantity"
                            name="quantity"
                            value={form.quantity}
                            min="0"
                            onChange={handleChange}
                            placeholder="Quantity"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          />
                        </div>
                      </div>
                      {/* Shipping */}
                      <div>
                        <label
                          htmlFor="shipping"
                          className="block text-sm font-semibold text-gray-900 mb-2"
                        >
                          Shipping
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiTruck className="text-gray-400" size={18} />
                          </div>
                          <select
                            id="shipping"
                            name="shipping"
                            value={form.shipping}
                            onChange={handleChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                          >
                            <option value="">Select option</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    {/* Available Sizes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Available Sizes */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Available Sizes
                        </label>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            type="text"
                            placeholder="e.g. S, XL, 42"
                            value={sizeInput}
                            onChange={(e) => setSizeInput(e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gray-900"
                          />

                          <button
                            type="button"
                            onClick={addSize}
                            className="w-full sm:w-auto h-12 sm:h-[50px] px-6 bg-black text-white rounded-lg hover:bg-gray-800 transition flex items-center justify-center font-medium"
                          >
                            Add
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-3">
                          {form.sizes.map((size) => (
                            <div
                              key={size}
                              className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2"
                            >
                              <span>{size}</span>

                              <button
                                type="button"
                                onClick={() => removeSize(size)}
                                className="text-red-600 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Available Colors */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Available Colors
                        </label>

                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            type="text"
                            placeholder="Color Name"
                            value={colorInput.name}
                            onChange={(e) =>
                              setColorInput({
                                ...colorInput,
                                name: e.target.value,
                              })
                            }
                            className="flex-1 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gray-900"
                          />

                          <input
                            type="color"
                            value={colorInput.hex}
                            onChange={(e) =>
                              setColorInput({
                                ...colorInput,
                                hex: e.target.value,
                              })
                            }
                            className="w-full sm:w-16 h-12 rounded-lg border cursor-pointer"
                          />

                          <button
                            type="button"
                            onClick={addColor}
                            className="w-full sm:w-auto h-12 sm:h-[50px] px-6 bg-black text-white rounded-lg hover:bg-gray-800 transition flex items-center justify-center font-medium whitespace-nowrap"
                          >
                            Add Color
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-3">
                          {form.colors.map((color) => (
                            <div
                              key={color.name}
                              className="flex items-center gap-2 border rounded-full px-3 py-2"
                            >
                              <div
                                className="w-5 h-5 rounded-full border"
                                style={{ background: color.hex }}
                              />

                              <span>{color.name}</span>

                              <button
                                type="button"
                                onClick={() => removeColor(color.name)}
                                className="text-red-600 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Description */}
                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-semibold text-gray-900 mb-2"
                      >
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        placeholder="Enter product description"
                        rows={4}
                        className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none"
                        required
                      />
                    </div>
                    {/* Photo upload */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <FiUpload /> Upload Photos (Max 1MB per photo)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        className="block w-full mt-2"
                      />
                      {/* Previews */}
                      <div className="flex gap-4 mt-3 flex-wrap">
                        {preview.length === 0 && (
                          <div className="rounded-lg bg-gray-100 p-4 text-gray-400 flex flex-col items-center justify-center">
                            <FiImage size={28} />
                            <span className="text-xs text-gray-400 mt-2">
                              No Media Uploaded
                            </span>
                          </div>
                        )}
                        {preview.map((src, i) => (
                          <div
                            key={i}
                            className="relative group w-20 flex-shrink-0"
                          >
                            <img
                              src={src}
                              alt="preview"
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              className="absolute top-1 right-1 bg-white/70 rounded-full text-red-600 shadow-md p-1 opacity-80 group-hover:opacity-100 transition"
                              title="Remove"
                            >
                              <FiX size={16} />
                            </button>
                            <div className="absolute left-1 bottom-1 text-[10px] bg-white/80 rounded px-1 py-0.5">
                              {(photos[i].size / 1024).toFixed(0)} KB
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="border-t border-gray-200 pt-6 mt-auto flex items-center justify-end gap-4">
                    <button
                      type="button"
                      onClick={() => window.history.back()}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Saving...
                        </span>
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateProduct;
